import React, {
    useCallback, useEffect, useLayoutEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import InputBase from '@mui/material/InputBase';
import Popper from '@mui/material/Popper';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import Translator from '%app/js/common/services/translator';
import useStableId from '../../services/useStableId';
import LabeledField from './LabeledField';
import usePlacesLibrary from './usePlacesLibrary';
import usePlaceSuggestions from './usePlaceSuggestions';
import {
    BLUR_CLOSE_DELAY_MS,
    INPUT_SX,
    ITEM_ACTIVE_SX,
    ITEM_BASE_SX,
    LIST_SX,
    PAPER_SX,
    POPPER_MIN_WIDTH,
    POPPER_SX,
    PRIMARY_TEXT_SX,
    SECONDARY_TEXT_SX,
    STATUS_SX,
} from './autocompleteStyles';

function WhereField({
    label, placeholder, value, onChange, onSelect, onCommit,
}) {
    const wrapperRef = useRef(null);
    const blurTimeoutRef = useRef(null);
    const valueRef = useRef(value);
    valueRef.current = value;
    const [open, setOpen] = useState(false);
    const [popperWidth, setPopperWidth] = useState(POPPER_MIN_WIDTH);
    const listboxId = useStableId('search-panel-where-list');
    const {
        placesLibrary, requestPlacesLibrary, markPlacesLibraryUnusable, reportPlacesQuotaExceeded,
    } = usePlacesLibrary();

    const clearBlurTimeout = useCallback(() => {
        if (blurTimeoutRef.current !== null) {
            clearTimeout(blurTimeoutRef.current);
            blurTimeoutRef.current = null;
        }
    }, []);

    useEffect(() => clearBlurTimeout, [clearBlurTimeout]);

    const commit = useCallback((nextValue) => {
        if (typeof onCommit === 'function') {
            onCommit(nextValue);
        }
    }, [onCommit]);

    const closeAndCommit = useCallback(() => {
        setOpen(false);
        commit(valueRef.current);
    }, [commit]);

    const handleSelect = useCallback((nextValue, placeId) => {
        if (typeof onSelect === 'function') {
            onSelect(nextValue, placeId);
        } else {
            onChange(nextValue);
            commit(nextValue);
        }
        setOpen(false);
        clearBlurTimeout();
    }, [onChange, onSelect, commit, clearBlurTimeout]);

    const {
        suggestions, activeIndex, loading, search, handleInputBlur, handleKeyDown, getSuggestionHandlers,
    } = usePlaceSuggestions({
        placesLibrary,
        onHighlight: onChange,
        onSelect: handleSelect,
        onUnusable: markPlacesLibraryUnusable,
        onQuotaExceeded: reportPlacesQuotaExceeded,
    });

    const handleChange = useCallback((event) => {
        onChange(event.target.value);
        search(event.target.value);
        setOpen(true);
    }, [onChange, search]);

    const handleFocus = useCallback(() => {
        clearBlurTimeout();
        setOpen(true);
        requestPlacesLibrary();
    }, [clearBlurTimeout, requestPlacesLibrary]);

    const handleBlur = useCallback(() => {
        handleInputBlur();
        clearBlurTimeout();
        if (placesLibrary === null) {
            closeAndCommit();
            return;
        }
        blurTimeoutRef.current = setTimeout(() => {
            blurTimeoutRef.current = null;
            closeAndCommit();
        }, BLUR_CLOSE_DELAY_MS);
    }, [clearBlurTimeout, closeAndCommit, handleInputBlur, placesLibrary]);

    useLayoutEffect(() => {
        if (open && wrapperRef.current) {
            const measured = wrapperRef.current.offsetWidth;
            setPopperWidth(Math.max(measured, POPPER_MIN_WIDTH));
        }
    }, [open]);

    const offersSuggestions = placesLibrary !== null;
    const showDropdown = open && (loading || suggestions.length > 0);
    const showList = showDropdown && !loading;
    const optionId = index => `${listboxId}-option-${index}`;

    return (
        <Box ref={wrapperRef} sx={{ width: '100%', minWidth: 0 }}>
            <LabeledField
                label={label}
                icon={<LocationOnIcon fontSize="small" />}
                active={open}
                renderInput={({ id }) => (
                    <InputBase
                        id={id}
                        type="text"
                        value={value}
                        placeholder={placeholder}
                        onChange={handleChange}
                        onKeyDown={handleKeyDown}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        inputProps={{
                            'data-testid': 'search-panel-where',
                            'aria-label': label,
                            role: offersSuggestions ? 'combobox' : undefined,
                            'aria-autocomplete': offersSuggestions ? 'list' : undefined,
                            'aria-expanded': offersSuggestions ? showList : undefined,
                            'aria-controls': offersSuggestions ? listboxId : undefined,
                            'aria-activedescendant': showList && activeIndex !== null ? optionId(activeIndex) : undefined,
                            autoComplete: 'off',
                        }}
                        sx={INPUT_SX}
                    />
                )}
            />
            <Popper
                open={showDropdown}
                anchorEl={wrapperRef.current}
                placement="bottom-start"
                sx={POPPER_SX}
                modifiers={[{ name: 'offset', options: { offset: [0, 4] } }]}
            >
                <Paper sx={{ ...PAPER_SX, width: popperWidth }} data-testid="search-panel-where-dropdown">
                    {loading ? (
                        <Box sx={STATUS_SX} data-testid="search-panel-where-loading">
                            {Translator.trans('search_panel.where.loading')}
                        </Box>
                    ) : (
                        <Box
                            component="ul"
                            id={listboxId}
                            sx={LIST_SX}
                            role="listbox"
                            aria-label={label}
                            data-testid="search-panel-where-list"
                        >
                            {suggestions.map((suggestion, index) => {
                                const isActive = index === activeIndex;
                                return (
                                    <Box
                                        key={suggestion.placeId}
                                        id={optionId(index)}
                                        component="li"
                                        role="option"
                                        aria-selected={isActive}
                                        sx={isActive ? { ...ITEM_BASE_SX, ...ITEM_ACTIVE_SX } : ITEM_BASE_SX}
                                        data-testid={`search-panel-where-option-${suggestion.placeId}`}
                                        {...getSuggestionHandlers(index)}
                                    >
                                        <Typography component="span" sx={PRIMARY_TEXT_SX}>
                                            {suggestion.mainText}
                                        </Typography>
                                        {suggestion.secondaryText ? (
                                            <Typography component="span" sx={SECONDARY_TEXT_SX}>
                                                {suggestion.secondaryText}
                                            </Typography>
                                        ) : null}
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                </Paper>
            </Popper>
        </Box>
    );
}

WhereField.propTypes = {
    label: PropTypes.string.isRequired,
    placeholder: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    onSelect: PropTypes.func,
    onCommit: PropTypes.func,
};

export default WhereField;
